import { Group, Paper, Select, type SelectProps } from "@mantine/core";

import { BillingReportCoreFilters } from "../../components/reports/BillingReportCoreFilters";
import type { DateRange } from "../../components/reports/ReportDateRangePicker";
import { ReportFileExportButton } from "../../components/reports/ReportFileExportButton";
import type {
  BillingReportAccountType,
  BillingReportDateBasis,
} from "../../services/reports.service";

interface CptFrequencyFilterPanelProps {
  dateBasis: BillingReportDateBasis;
  accountType: BillingReportAccountType;
  dateRange: DateRange;
  pathologistOptions: SelectProps["data"];
  practiceOptions: SelectProps["data"];
  physicianOptions: SelectProps["data"];
  surgeryCenterOptions: SelectProps["data"];
  prefixOptions: SelectProps["data"];
  billingScenarioOptions: SelectProps["data"];
  pathologistId: string | null;
  practiceId: string | null;
  physicianId: string | null;
  surgeryCenterLabId: string | null;
  prefixId: string | null;
  billingScenarioId: string | null;
  excelFilename: string;
  pdfFilename: string;
  exportDisabled: boolean;
  onDateBasisChange: (value: string | null) => void;
  onAccountTypeChange: (value: string | null) => void;
  onDateRangeChange: (value: DateRange) => void;
  onPathologistChange: (value: string | null) => void;
  onPracticeChange: (value: string | null) => void;
  onPhysicianChange: (value: string | null) => void;
  onSurgeryCenterChange: (value: string | null) => void;
  onPrefixChange: (value: string | null) => void;
  onBillingScenarioChange: (value: string | null) => void;
  onExcelExport: () => Promise<Blob>;
  onPdfExport: () => Promise<Blob>;
}

export function CptFrequencyFilterPanel({
  dateBasis,
  accountType,
  dateRange,
  pathologistOptions,
  practiceOptions,
  physicianOptions,
  surgeryCenterOptions,
  prefixOptions,
  billingScenarioOptions,
  pathologistId,
  practiceId,
  physicianId,
  surgeryCenterLabId,
  prefixId,
  billingScenarioId,
  excelFilename,
  pdfFilename,
  exportDisabled,
  onDateBasisChange,
  onAccountTypeChange,
  onDateRangeChange,
  onPathologistChange,
  onPracticeChange,
  onPhysicianChange,
  onSurgeryCenterChange,
  onPrefixChange,
  onBillingScenarioChange,
  onExcelExport,
  onPdfExport,
}: CptFrequencyFilterPanelProps) {
  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Group align="flex-end" wrap="wrap" gap="md">
          <BillingReportCoreFilters
            dateBasis={dateBasis}
            accountType={accountType}
            dateRange={dateRange}
            onDateBasisChange={onDateBasisChange}
            onAccountTypeChange={onAccountTypeChange}
            onDateRangeChange={onDateRangeChange}
          />
          <Select
            label="Pathologist"
            placeholder="All pathologists"
            data={pathologistOptions}
            value={pathologistId}
            onChange={onPathologistChange}
            searchable
            clearable
            w={200}
          />
          <Select
            label="Practice"
            placeholder="All practices"
            data={practiceOptions}
            value={practiceId}
            onChange={onPracticeChange}
            searchable
            clearable
            w={200}
          />
          <Select
            label="Referring Physician"
            placeholder="All physicians"
            data={physicianOptions}
            value={physicianId}
            onChange={onPhysicianChange}
            searchable
            clearable
            w={200}
          />
          <Select
            label="Surgery Center"
            placeholder="All surgery centers"
            data={surgeryCenterOptions}
            value={surgeryCenterLabId}
            onChange={onSurgeryCenterChange}
            searchable
            clearable
            w={200}
          />
          <Select
            label="Case Prefix"
            placeholder="All prefixes"
            data={prefixOptions}
            value={prefixId}
            onChange={onPrefixChange}
            searchable
            clearable
            w={180}
          />
          <Select
            label="Billing Scenario"
            placeholder="All scenarios"
            data={billingScenarioOptions}
            value={billingScenarioId}
            onChange={onBillingScenarioChange}
            searchable
            clearable
            w={200}
          />
        </Group>
        <Group gap="xs">
          <ReportFileExportButton
            label="Export Excel"
            filename={excelFilename}
            fetchFile={onExcelExport}
            disabled={exportDisabled}
          />
          <ReportFileExportButton
            label="Export PDF"
            filename={pdfFilename}
            fetchFile={onPdfExport}
            disabled={exportDisabled}
          />
        </Group>
      </Group>
    </Paper>
  );
}
